import { useEffect, useState } from "react";
import { Curriculum } from "../../../../types/dataModel";
import { API, CURRICULUM_TYPE } from "../../../../common/Constants";
import useFetch from "../../../../hooks/useFetch";
import PreLoader from "../../../../core/common/preLoader";
import {
  getAPIHeaders,
  isBeforeCurrentDate,
  isNotEmpty,
  toDateFromEpoch,
} from "../../../../common/Utils";
import { Link } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import CommonSelect from "../../../../core/common/commonSelect";

interface Props {
  onSuccess: () => void;
}

const AddCurriculum = ({ onSuccess }: Props) => {
  const [curriculum, setCurriculum] = useState<Curriculum>();
  const [successCurriculum, setSuccessCurriculum] = useState<Curriculum>(); // Store curriculum for success message
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const { isLoading, error, data, status, callFetch } = useFetch();
  const [showMsgBox, setShowMsgBox] = useState<boolean>(false);

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  useEffect(() => {
    if (data && status && status < 299) {
      setSuccessCurriculum({ ...curriculum });
      reset();
      setShowMsgBox(true);
      onSuccess();
    }
  }, [status]);

  useEffect(() => {
    if (error) {
      if (error.response?.data) {
        setErrorMessage(error.response?.data);
      } else {
        setErrorMessage("Something went wrong please try again later.");
      }
    }
  }, [error]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (!isNotEmpty(curriculum?.title)) {
      setErrorMessage("Please provide title");
    } else if (!isNotEmpty(curriculum?.code)) {
      setErrorMessage("Please provide code");
    } else if (!isNotEmpty(curriculum?.type)) {
      setErrorMessage("Please select type");
    } else if (
      !curriculum?.startDate ||
      isBeforeCurrentDate(curriculum?.startDate)
    ) {
      setErrorMessage("Please select start date greater than current date");
    } else if (
      !curriculum?.endDate ||
      isBeforeCurrentDate(curriculum?.endDate)
    ) {
      setErrorMessage("Please select end date greater than current date");
    } else {
      setErrorMessage("");
      callFetch(API.CURRICULUM, "POST", getAPIHeaders(), null, {
        ...curriculum,
      }).catch((err) => {
        setErrorMessage(err.response?.data || "Something went wrong, please try again later.");
      });
    }
  };

  const reset = () => {
    setCurriculum({
      title: "",
      description: "",
      code: "",
      type: "",
      startDate: undefined,
      endDate: undefined,
    });
    setErrorMessage("");
    setShowMsgBox(false);
  };

  const renderForm = () => {
    return (
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Add Curriculum</h4>
          <button
            type="button"
            className="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={reset}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <form>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Title <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={curriculum?.title}
                    onChange={(e) => {
                      setCurriculum({
                        ...curriculum,
                        title: e.target.value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    value={curriculum?.description}
                    onChange={(e) => {
                      setCurriculum({
                        ...curriculum,
                        description: e.target.value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Code <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={curriculum?.code}
                    onChange={(e) => {
                      setCurriculum({
                        ...curriculum,
                        code: e.target.value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Type <span className="text-danger"> *</span>
                  </label>
                  <CommonSelect
                    className="select"
                    options={CURRICULUM_TYPE}
                    defaultValue={CURRICULUM_TYPE[0]}
                    onChange={(value) => {
                      setCurriculum({
                        ...curriculum,
                        type: value,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Start Date <span className="text-danger"> *</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      className="form-control datetimepicker"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                      value={
                        curriculum?.startDate
                          ? dayjs(toDateFromEpoch(curriculum?.startDate))
                          : undefined
                      }
                      onChange={(dt) => {
                        setCurriculum({
                          ...curriculum,
                          startDate: dt?.toDate()?.getTime(),
                        });
                      }}
                      disabledDate={(current) => {
                        return current && current < dayjs().startOf("day");
                      }}
                    />
                    <span className="input-icon-addon">
                      <i className="ti ti-calendar text-gray-7" />
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    End Date <span className="text-danger"> *</span>
                  </label>
                  <div className="input-icon-end position-relative">
                    <DatePicker
                      className="form-control datetimepicker"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                      value={
                        curriculum?.endDate
                          ? dayjs(toDateFromEpoch(curriculum?.endDate))
                          : undefined
                      }
                      onChange={(dt) => {
                        setCurriculum({
                          ...curriculum,
                          endDate: dt?.toDate()?.getTime(),
                        });
                      }}
                      disabledDate={(current) => {
                        return (
                          current &&
                          (curriculum?.startDate
                            ? current <
                              dayjs(toDateFromEpoch(curriculum?.startDate))
                            : current < dayjs().startOf("day"))
                        );
                      }}
                    />
                    <span className="input-icon-addon">
                      <i className="ti ti-calendar text-gray-7" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-footer"
            style={{
              justifyContent: "space-between",
              flexDirection: "row",
            }}
          >
            <div style={{ maxWidth: "50%" }}>
              {errorMessage ? (
                <>
                  <i className="ti ti-alert-triangle text-danger" />
                  <span className="text-danger"> {errorMessage} </span>
                </>
              ) : (
                <></>
              )}
            </div>
            <div>
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={reset}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                Add Curriculum
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  };

  const renderMsgBox = () => {
    return (
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center">
            <span className="avatar avatar-xl bg-transparent-success text-success mb-3">
              <i className="ti ti-thumb-up fs-36" />
            </span>
            <h4 className="mb-1">
              Curriculum "{successCurriculum?.title}" with code "
              {successCurriculum?.code}" has been successfully added!
            </h4>
            <div className="d-flex justify-content-center">
              <Link
                to="#"
                data-bs-dismiss="modal"
                className="btn btn-success"
                onClick={() => {
                  setTimeout(() => {
                    setShowMsgBox(false);
                  }, 1000);
                }}
              >
                Close
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="modal fade"
      id="add_curriculum"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered modal-md">
        <PreLoader showLoader={isLoading} />
        {showMsgBox ? renderMsgBox() : renderForm()}
      </div>
    </div>
  );
};

export default AddCurriculum;
